import React, { useState, useEffect, useCallback } from 'react';
import { useWallet } from '../context/WalletContext';
import { useCurrency } from '../context/CurrencyContext';
import { toast } from 'sonner';
import { 
  AlertTriangle, 
  TrendingUp, 
  ArrowDownToLine, 
  ArrowUpFromLine,
  Loader2,
  CheckCircle,
  XCircle,
  ExternalLink,
  Shield,
  Wallet,
  RefreshCw
} from 'lucide-react';
import { 
  uintCV, 
  principalCV,
  contractPrincipalCV,
  noneCV,
  boolCV,
  Pc
} from '@stacks/transactions';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ============== REAL PROTOCOL CONTRACTS (MAINNET) ==============

// Zest Protocol v2 Contracts
// Important: For Zest V2, users call the MARKET contract, NOT vault contracts directly.
// The market contract routes to vaults internally.
const ZEST_DEPLOYER = "SP1A27KFY4XERQCCRCARCYD1CC5N7M6688BSYADJ7";
const ZEST_CONTRACTS = {
  market: `${ZEST_DEPLOYER}.v0-4-market`,
  vault_sbtc: `${ZEST_DEPLOYER}.v0-vault-sbtc`,
  vault_stx: `${ZEST_DEPLOYER}.v0-vault-stx`,
  vault_ststx: `${ZEST_DEPLOYER}.v0-vault-ststx`,
};

// Token contracts for Zest supply-collateral-add
const TOKEN_CONTRACTS = {
  wstx: "SP3K8BC0PPEVCV7NZ6QSRWPQ2JE9E5B6N3PA0KBR9.token-wstx",
  sbtc: "SM3VDXK3WZZSA84XXFKAFAF15NNZX32CTSG82JFQ4.sbtc-token",
  ststx: "SP4SZE494VC2YC5JYG7AYFQ44F5Q4PYV7DVMDPBG.ststx-token",
};

// StackingDAO Contracts
const STACKINGDAO_DEPLOYER = "SP4SZE494VC2YC5JYG7AYFQ44F5Q4PYV7DVMDPBG";
const STACKINGDAO_CONTRACTS = {
  core_v6: `${STACKINGDAO_DEPLOYER}.stacking-dao-core-v6`,
  reserve: `${STACKINGDAO_DEPLOYER}.reserve-v1`,
};

// ALEX DEX Contracts
const ALEX_DEPLOYER = "SP3K8BC0PPEVCV7NZ6QSRWPQ2JE9E5B6N3PA0KBR9";
const ALEX_CONTRACTS = {
  amm_swap_pool: `${ALEX_DEPLOYER}.amm-swap-pool-v1-1`,
  alex_reserve_pool: `${ALEX_DEPLOYER}.alex-reserve-pool`,
};

// Hermetica Protocol Contracts (hBTC Vault v1-1 - active vault)
const HERMETICA_DEPLOYER = "SP1S1HSFH0SQQGWKB69EYFNY0B1MHRMGXR3J1FH4D";
const HERMETICA_CONTRACTS = {
  vault_hbtc: `${HERMETICA_DEPLOYER}.vault-hbtc-v1-1`,  // Updated from v1 (deprecated) to v1-1 (active)
  token_hbtc: `${HERMETICA_DEPLOYER}.token-hbtc`,
  reserve: `${HERMETICA_DEPLOYER}.reserve-hbtc-v1`,
};

// sBTC token contract (used by Hermetica)
const SBTC_TOKEN = "SM3VDXK3WZZSA84XXFKAFAF15NNZX32CTSG82JFQ4.sbtc-token";

// Bitflow DEX Contracts
const BITFLOW_DEPLOYER = "SPQC38PW542EQJ5M11CR25P7BS1CA6QT4TBXGB3M";
const BITFLOW_CONTRACTS = {
  stableswap_stx_ststx: `${BITFLOW_DEPLOYER}.stableswap-stx-ststx-v-1-2`,
  lp_token_stx_ststx: `${BITFLOW_DEPLOYER}.stx-ststx-lp-token-v-1-2`,
  earn_stx_ststx: `${BITFLOW_DEPLOYER}.earn-stx-ststx-v-1-2`,
};
// StackingDAO stSTX token for Bitflow pool
const STSTX_TOKEN = "SP4SZE494VC2YC5JYG7AYFQ44F5Q4PYV7DVMDPBG.ststx-token";

// Velar DEX Contracts (UniV2-style AMM)
const VELAR_DEPLOYER = "SP1Y5YSTAHZ88XYK1VPDH24GY0HPX5J4JECTMY4A1";
const VELAR_CONTRACTS = {
  router: `${VELAR_DEPLOYER}.univ2-router`,
  core: `${VELAR_DEPLOYER}.univ2-core`,
  velar_token: `${VELAR_DEPLOYER}.velar-token`,
  wstx: `${VELAR_DEPLOYER}.wstx`,
  velar_stx_lp: `${VELAR_DEPLOYER}.velar-stx`,
};
const VELAR_STX_POOL_ID = 21; // Pool ID for VELAR-STX pair

// STXOSHI Coin Vault (Treasury-based staking with 25% APY)
const STXOSHI_TREASURY = "SP3VVPEFZNN84EGMD0GN4XWAZ4X5ZMWC852NGWVWN";
const STXOSHI_TOKEN = "SP1Z92MPDQEWZXW36VX71Q25HKF5K2EPCJ304F275.tokensoft-token-v4karmaedcr";
const STXOSHI_TOKEN_NAME = "tokensoft-token"; // The ft identifier in the contract
const STXOSHI_APY = 25; // 25% APY
const STXOSHI_USD = 0.0000037; // STXOSHI → USD (matches bot dashboards, app-wide constant)
const STXOSHI_DECIMALS = 6; // STXOSHI uses 6 decimals
const STXOSHI_MAX_DEPOSIT = 1_000_000_000_000; // 1,000,000 STXOSHI coins (with 6 decimals)

// Protocol logos/colors
const PROTOCOL_CONFIG = {
  zest: {
    name: 'Zest Protocol',
    color: 'bg-blue-500',
    textColor: 'text-blue-400',
    description: 'Lending protocol for sBTC, STX, and stSTX'
  },
  stackingdao: {
    name: 'StackingDAO',
    color: 'bg-purple-500',
    textColor: 'text-purple-400',
    description: 'Liquid staking for STX → stSTX'
  },
  alex: {
    name: 'ALEX DEX',
    color: 'bg-orange-500',
    textColor: 'text-orange-400',
    description: 'Decentralized exchange & AMM liquidity'
  },
  hermetica: {
    name: 'Hermetica',
    color: 'bg-emerald-500',
    textColor: 'text-emerald-400',
    description: 'sBTC yield & BTC-backed stablecoin'
  },
  bitflow: {
    name: 'Bitflow',
    color: 'bg-cyan-500',
    textColor: 'text-cyan-400',
    description: 'DEX aggregator & liquidity protocol'
  },
  velar: {
    name: 'Velar DEX',
    color: 'bg-pink-500',
    textColor: 'text-pink-400',
    description: 'Paired liquidity pools (VELAR + STX)',
    isPaired: true  // Indicates this requires two assets
  },
  stxoshi: {
    name: 'STXOSHI Vault',
    color: 'bg-gradient-to-r from-amber-500 to-orange-600',
    textColor: 'text-amber-400',
    description: 'Native STXOSHI coin staking',
    isHighYield: true,
    apy: 25,
    badge: 'High Yield 25%'
  }
};

// Risk Warning Modal
function RiskWarningModal({ isOpen, onAccept, onDecline, warning }) {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-zinc-900 border border-red-500/50 rounded-2xl p-5 max-w-md w-full">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
          <h2 className="text-lg font-bold text-red-400">Risk Warning</h2>
        </div>
        
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 max-h-48 overflow-y-auto">
          <pre className="text-xs text-red-300 whitespace-pre-wrap font-sans leading-relaxed">
            {warning}
          </pre>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onDecline}
            className="flex-1 py-2.5 px-4 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onAccept}
            className="flex-1 py-2.5 px-4 bg-red-600 hover:bg-red-500 rounded-lg text-white text-sm font-medium transition-colors"
          >
            Accept & Continue
          </button>
        </div>
      </div>
    </div>
  );
}

// Deposit Form
function DepositForm({ agentId, onSuccess, getAuthHeaders }) {
  const { signStxTransfer, signContractCall, addresses, isConnected } = useWallet();
  const [protocol, setProtocol] = useState('stackingdao');
  const [asset, setAsset] = useState('stx');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmingDeposit, setConfirmingDeposit] = useState(false);
  const [apys, setApys] = useState({});
  const [showRiskWarning, setShowRiskWarning] = useState(false);
  const [riskWarning, setRiskWarning] = useState('');
  const [pendingDeposit, setPendingDeposit] = useState(null);
  const [failedDeposit, setFailedDeposit] = useState(null); // Track deposits that succeeded on-chain but failed to confirm
  const [walletPopupFailed, setWalletPopupFailed] = useState(false); // Track if wallet popup didn't appear
  const [recovering, setRecovering] = useState(false); // Track self-recovery in progress
  const [showManualRecovery, setShowManualRecovery] = useState(false); // Show manual TX recovery form
  const [manualTxId, setManualTxId] = useState(''); // Manual TX ID input

  useEffect(() => {
    // Fetch APYs
    fetch(`${API}/defi/apys`)
      .then(r => r.json())
      .then(data => setApys(data.apys))
      .catch(console.error);
      
    // Fetch risk warning
    fetch(`${API}/defi/risk-warning`)
      .then(r => r.json())
      .then(data => setRiskWarning(data.warning))
      .catch(console.error);
    
    // Load any failed deposits from localStorage for this agent
    const savedFailedDeposits = JSON.parse(localStorage.getItem('failedDeposits') || '[]');
    const agentFailedDeposit = savedFailedDeposits.find(d => d.agentId === agentId);
    if (agentFailedDeposit) {
      setFailedDeposit(agentFailedDeposit);
    }
  }, [agentId]);

  // Set the correct default asset when protocol changes
  useEffect(() => {
    const protocolDefaults = {
      stackingdao: 'stx',
      zest: 'ststx',
      alex: 'alex',
      hermetica: 'sbtc',
      bitflow: 'stx',
      velar: 'velar',
      stxoshi: 'stxoshi'
    };
    setAsset(protocolDefaults[protocol] || 'stx');
    setAmount(''); // Clear amount when protocol changes
  }, [protocol]);

  const getAssetOptions = () => {
    if (protocol === 'stackingdao') return [{ value: 'stx', label: 'STX' }];
    // Zest V2 market only supports stSTX and sBTC, NOT raw STX
    // Users need to convert STX → stSTX via StackingDAO first
    if (protocol === 'zest') return [
      { value: 'ststx', label: 'stSTX (Liquid Staked STX)' },
      { value: 'sbtc', label: 'sBTC' }
    ];
    if (protocol === 'alex') return [
      { value: 'alex', label: 'ALEX Gov Token' }
    ];
    if (protocol === 'hermetica') return [
      { value: 'sbtc', label: 'sBTC' },
      { value: 'stx', label: 'STX' }
    ];
    if (protocol === 'bitflow') return [
      { value: 'stx', label: 'STX (Single-Sided)' }  // Single-sided STX deposit to STX-stSTX pool
    ];
    if (protocol === 'velar') return [
      { value: 'velar', label: 'VELAR (+ STX)' }  // Paired liquidity - VELAR is primary input
    ];
    if (protocol === 'stxoshi') return [
      { value: 'stxoshi', label: 'STXOSHI Coin' }
    ];
    return [{ value: 'stx', label: 'STX' }];
  };

  // No deposit limits - users can deposit any amount
  const getMaxAmount = () => {
    return 999999999; // Effectively unlimited
  };

  const getCurrentApy = () => {
    // STXOSHI has fixed 25% APY
    if (protocol === 'stxoshi') return STXOSHI_APY;
    const key = `${protocol}_${asset}`;
    return apys[key] || 0;
  };

  const handleInitiateDeposit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    // Show risk warning first
    setShowRiskWarning(true);
  };

  const handleAcceptRisk = async () => {
    setShowRiskWarning(false);
    setLoading(true);
    
    try {
      // Check if wallet is connected and signContractCall is available
      if (!isConnected || !signContractCall) {
        throw new Error('Wallet not connected. Please connect your wallet first.');
      }
      
      // Log the connected address for debugging
      console.log('[DEFI] Connected wallet address:', addresses.stx);
      
      // Convert amount to micro units
      // STX, stSTX, and STXOSHI use 6 decimals; sBTC and ALEX use 8 decimals
      let microAmount;
      if (asset === 'stx' || asset === 'ststx' || asset === 'stxoshi' || asset === 'velar') {
        microAmount = Math.floor(parseFloat(amount) * 1_000_000);  // 6 decimals
      } else {
        microAmount = Math.floor(parseFloat(amount) * 100_000_000); // 8 decimals (sBTC, ALEX)
      }
      
      toast.info(`Depositing from ${addresses.stx?.slice(0, 8)}...${addresses.stx?.slice(-4)}`, {
        description: `Please approve the transaction in your wallet`
      });
      
      // Build the contract call based on protocol
      const contractCall = buildContractCall(protocol, asset, microAmount, addresses.stx);
      
      if (!contractCall) {
        throw new Error(`Protocol ${protocol} not supported for ${asset} deposits`);
      }
      
      console.log('[DEFI] Executing contract call:', contractCall);
      
      // Reset wallet popup failed state
      setWalletPopupFailed(false);
      
      // Set a timeout to show retry button if wallet doesn't respond
      const walletTimeout = setTimeout(() => {
        setWalletPopupFailed(true);
      }, 8000);
      
      let txResult;
      
      // Handle STXOSHI treasury deposits differently (token transfer, not contract call)
      if (contractCall.type === 'treasury-deposit') {
        console.log('[DEFI] STXOSHI treasury deposit - using token transfer');
        
        // For STXOSHI, we do a simple token transfer to treasury
        // This uses the wallet's token transfer capability
        const { request } = await import('sats-connect');
        const { cvToHex, postConditionToHex } = await import('@stacks/transactions');
        
        // Build ft-transfer contract call to the token contract
        const [tokenAddr, tokenName] = contractCall.tokenContract.split('.');
        
        const transferPayload = {
          contract: contractCall.tokenContract,
          functionName: 'transfer',
          arguments: [
            cvToHex(uintCV(contractCall.amount)),
            cvToHex(principalCV(addresses.stx)),
            cvToHex(principalCV(contractCall.treasuryAddress)),
            cvToHex(noneCV()) // memo
          ],
          postConditions: contractCall.postConditions.map(pc => postConditionToHex(pc)),
          postConditionMode: 'deny'
        };
        
        console.log('[DEFI] STXOSHI transfer payload:', transferPayload);
        
        // Show toast before wallet popup (same as deployment)
        toast.info('Please approve the transaction in your wallet', {
          description: `${(contractCall.amount / 1_000_000).toLocaleString()} STXOSHI deposit`,
          duration: 15000
        });
        
        const response = await request('stx_callContract', transferPayload);
        clearTimeout(walletTimeout);
        
        if (response && (response.status === 'success' || response.result)) {
          txResult = { 
            success: true, 
            txid: response.result?.txid || response.txid 
          };
          setWalletPopupFailed(false);
        } else {
          throw new Error(response?.error?.message || 'STXOSHI transfer failed');
        }
      } else {
        // Execute the real contract call to the DeFi protocol
        txResult = await signContractCall(contractCall);
        clearTimeout(walletTimeout);
        setWalletPopupFailed(false);
      }
      
      console.log('[DEFI] Transaction result:', txResult);
      
      if (txResult && txResult.success && txResult.txid) {
        // Show confirming state
        setConfirmingDeposit(true);
        toast.info('Transaction sent! Registering deposit...', {
          description: 'Waiting for blockchain confirmation'
        });
        
        // Step 1: Register the pending deposit with backend
        let positionId = null;
        try {
          console.log('[DEFI] Registering pending deposit with backend...');
          
          const confirmResponse = await fetch(
            `${API}/agents/${agentId}/defi/deposit/confirm?txid=${txResult.txid}&protocol=${protocol}&asset=${asset}&amount=${microAmount}`,
            {
              method: 'POST',
              headers: getAuthHeaders()
            }
          );
          
          if (confirmResponse.ok) {
            const confirmData = await confirmResponse.json();
            positionId = confirmData.position_id;
            console.log('[DEFI] Deposit registered as pending, position ID:', positionId);
            
            toast.info('Deposit registered! Verifying on-chain...', {
              description: 'This may take 1-2 minutes for blockchain confirmation'
            });
          } else {
            const errorData = await confirmResponse.json().catch(() => ({}));
            throw new Error(errorData.detail || 'Failed to register deposit');
          }
        } catch (registerError) {
          console.error('[DEFI] Failed to register deposit:', registerError);
          // Store for recovery
          const failedDepositInfo = {
            txid: txResult.txid,
            protocol,
            asset,
            amount: microAmount,
            amountDisplay: amount,
            timestamp: new Date().toISOString(),
            error: registerError.message
          };
          setFailedDeposit(failedDepositInfo);
          const savedFailedDeposits = JSON.parse(localStorage.getItem('failedDeposits') || '[]');
          savedFailedDeposits.push({ ...failedDepositInfo, agentId });
          localStorage.setItem('failedDeposits', JSON.stringify(savedFailedDeposits));
          
          toast.warning('Deposit sent but registration failed!', {
            description: 'Your transaction is on-chain. Contact support with your TX ID.',
            duration: 10000
          });
          setConfirmingDeposit(false);
          setAmount('');
          onSuccess?.();
          return;
        }
        
        // Step 2: Poll for on-chain verification
        const maxVerifyAttempts = 30; // Max 30 attempts (~2.5 minutes)
        const verifyInterval = 5000; // Check every 5 seconds
        let verified = false;
        let verifyError = null;
        
        for (let attempt = 1; attempt <= maxVerifyAttempts; attempt++) {
          try {
            console.log(`[DEFI] Verification attempt ${attempt}/${maxVerifyAttempts}`);
            
            const verifyResponse = await fetch(
              `${API}/agents/${agentId}/defi/deposit/verify?txid=${txResult.txid}`,
              {
                method: 'GET',
                headers: getAuthHeaders()
              }
            );
            
            if (verifyResponse.ok) {
              const verifyData = await verifyResponse.json();
              
              if (verifyData.position_status === 'active') {
                // SUCCESS! Transaction confirmed on-chain
                verified = true;
                setConfirmingDeposit(false);
                setFailedDeposit(null);
                toast.success('Deposit confirmed on-chain!', {
                  description: `Your ${amount} ${asset.toUpperCase()} is now earning yield on ${PROTOCOL_CONFIG[protocol]?.name}!`,
                  duration: 8000
                });
                break;
              } else if (verifyData.position_status === 'failed') {
                // Transaction FAILED on-chain (e.g., post-condition error)
                verifyError = verifyData.failure_reason || 'Transaction failed on-chain';
                console.error('[DEFI] Transaction failed on-chain:', verifyError);
                
                setConfirmingDeposit(false);
                toast.error('Deposit transaction failed!', {
                  description: verifyData.message || 'The transaction was rejected by the blockchain. Your funds were NOT transferred.',
                  duration: 12000
                });
                break;
              }
              // Still pending - continue polling
            }
            
            // Wait before next check
            if (attempt < maxVerifyAttempts) {
              await new Promise(r => setTimeout(r, verifyInterval));
            }
          } catch (verifyErr) {
            console.warn(`[DEFI] Verification check ${attempt} error:`, verifyErr);
            // Continue trying
            if (attempt < maxVerifyAttempts) {
              await new Promise(r => setTimeout(r, verifyInterval));
            }
          }
        }
        
        setConfirmingDeposit(false);
        
        if (!verified && !verifyError) {
          // Timeout - transaction still pending after max attempts
          toast.warning('Verification timeout', {
            description: 'Transaction is still pending. It may take a few more minutes. Check your positions for status.',
            duration: 10000
          });
        }
        
        setAmount('');
        onSuccess?.();
      } else {
        throw new Error('Transaction was not successful');
      }
    } catch (error) {
      console.error('Deposit error:', error);
      
      // If contract call fails, provide helpful error message
      if (error.message.includes('rejected') || error.message.includes('denied')) {
        toast.error('Transaction cancelled', { description: 'You rejected the transaction in your wallet' });
      } else if (error.message.includes('insufficient')) {
        toast.error('Insufficient balance', { description: 'You do not have enough funds for this deposit' });
      } else {
        toast.error('Deposit failed', { description: error.message || 'Unknown error' });
      }
    } finally {
      setLoading(false);
      setPendingDeposit(null);
    }
  };

  // Build contract call parameters for each protocol
  // Arguments should be Clarity values (uintCV, principalCV, etc.) for openContractCall
  const buildContractCall = (protocol, asset, amount, sender) => {
    switch (protocol) {
      case 'zest':
        return buildZestDeposit(asset, amount, sender);
      case 'stackingdao':
        return buildStackingDAODeposit(amount, sender);
      case 'alex':
        return buildALEXDeposit(asset, amount, sender);
      case 'hermetica':
        return buildHermeticaDeposit(asset, amount, sender);
      case 'bitflow':
        return buildBitflowDeposit(asset, amount, sender);
      case 'velar':
        return buildVelarDeposit(asset, amount, sender);
      case 'stxoshi':
        return buildSTXOSHIDeposit(asset, amount, sender);
      default:
        return null;
    }
  };

  // ZEST PROTOCOL - Supply to lending market
  // Uses the v0-4-market contract with supply-collateral-add function
  // Note: Zest V2 only supports stSTX and sBTC - NOT raw STX!
  // Reference: https://docs.zestprotocol.com/start/stacks-market-v2-smart-contracts-overview/v2-contracts/market-contracts/market
  const buildZestDeposit = (asset, amount, sender) => {
    // Zest V2 market only supports these tokens
    const tokenMap = {
      sbtc: { contract: TOKEN_CONTRACTS.sbtc, name: 'sbtc-token' },
      ststx: { contract: TOKEN_CONTRACTS.ststx, name: 'ststx' },
    };
    
    const tokenInfo = tokenMap[asset];
    if (!tokenInfo) {
      console.error('[ZEST] Unsupported asset for Zest V2:', asset);
      toast.error('Zest V2 only supports stSTX and sBTC', {
        description: 'To deposit STX, first convert to stSTX via StackingDAO'
      });
      return null;
    }
    
    // Parse token contract into address and name
    const [ftAddress, ftName] = tokenInfo.contract.split('.');
    
    // Market contract
    const [contractAddress, contractName] = ZEST_CONTRACTS.market.split('.');
    
    // Post-condition: Allow sending the token to Zest
    // Using Pc builder for fungible token post-condition
    const postConditions = [
      Pc.principal(sender)
        .willSendEq(amount)
        .ft(tokenInfo.contract, tokenInfo.name)
    ];
    
    // supply-collateral-add takes: ft, amount, min-shares, price-feeds (optional)
    // min-shares: we use 1 (minimum expected shares, for slippage protection)
    // price-feeds: none (optional parameter)
    return {
      contractAddress,
      contractName,
      functionName: 'supply-collateral-add',
      functionArgs: [
        contractPrincipalCV(ftAddress, ftName),  // ft - token contract
        uintCV(amount),                           // amount to supply
        uintCV(1),                                // min-shares (slippage protection)
        noneCV(),                                 // price-feeds (optional, none)
      ],
      postConditions,
      // Zest market contract moves tokens internally (user -> market -> vault)
      // We need to allow these internal transfers while still protecting user's principal
      postConditionMode: 'allow',
      stxAmount: 0  // No raw STX transfer - user must approve token transfer
    };
  };

  // STACKINGDAO - Deposit STX for liquid staking (receive stSTX)
  // Reference: https://docs.stackingdao.com/stackingdao/the-stacking-dao-app/ststx-liquid-stacking-with-stx-rewards
  const buildStackingDAODeposit = (amount, sender) => {
    const [contractAddress, contractName] = STACKINGDAO_CONTRACTS.core_v6.split('.');
    
    // StackingDAO deposit function takes 7 arguments:
    // 1. reserve - trait reference to reserve contract
    // 2. commission-contract - trait reference to commission contract  
    // 3. staking-contract - trait reference to staking contract
    // 4. direct-helpers - trait reference to direct helpers contract
    // 5. stx-amount - uint amount to deposit
    // 6. referrer - optional principal (none for no referral)
    // 7. pool - optional principal (none for default pool)
    
    const STACKINGDAO_BASE = "SP4SZE494VC2YC5JYG7AYFQ44F5Q4PYV7DVMDPBG";
    
    // Post-condition: Allow sending exactly the specified amount of STX
    // Using Pc builder (v7 API)
    const postConditions = [
      Pc.principal(sender).willSendEq(amount).ustx()
    ];
    
    return {
      contractAddress,
      contractName,
      functionName: 'deposit',
      functionArgs: [
        contractPrincipalCV(STACKINGDAO_BASE, "reserve-v1"),           // reserve
        contractPrincipalCV(STACKINGDAO_BASE, "commission-v2"),        // commission-contract
        contractPrincipalCV(STACKINGDAO_BASE, "staking-v0"),           // staking-contract
        contractPrincipalCV(STACKINGDAO_BASE, "direct-helpers-v4"),    // direct-helpers
        uintCV(amount),                                                 // stx-amount
        noneCV(),                                                       // referrer (none)
        noneCV(),                                                       // pool (none)
      ],
      postConditions,
      stxAmount: amount
    };
  };

  // ALEX DEX - Stake ALEX governance tokens for yield
  // Users must have the age000-governance-token (ALEX's actual governance token)
  // This earns staking rewards from the ALEX protocol
  // Reference: https://app.alexlab.co/
  const buildALEXDeposit = (asset, amount, sender) => {
    const [contractAddress, contractName] = ALEX_CONTRACTS.alex_reserve_pool.split('.');
    
    // The ALEX governance token that can be staked in alex-reserve-pool
    // This is the "ALEX" token shown in wallets as "ALEX's governance token"
    const ALEX_GOV_TOKEN = "SP3K8BC0PPEVCV7NZ6QSRWPQ2JE9E5B6N3PA0KBR9.age000-governance-token";
    const [tokenAddress, tokenName] = ALEX_GOV_TOKEN.split('.');
    
    // Post-condition: Allow sending the specified amount of ALEX governance tokens
    const postConditions = [
      Pc.principal(sender)
        .willSendLte(amount)
        .ft(ALEX_GOV_TOKEN, 'alex')
    ];
    
    // stake-tokens function takes:
    // 1. token-trait - the ALEX governance token contract
    // 2. amount-token - amount to stake (in micro units - ALEX uses 8 decimals)
    // 3. lock-period - staking period (u1 = 1 cycle, u4 = 4 cycles, etc.)
    return {
      contractAddress,
      contractName,
      functionName: 'stake-tokens',
      functionArgs: [
        contractPrincipalCV(tokenAddress, tokenName), // token-trait (governance token)
        uintCV(amount),        // amount-token
        uintCV(1),             // lock-period (1 cycle = shorter commitment)
      ],
      postConditions,
      postConditionMode: 'allow', // Allow mode for staking
      stxAmount: 0  // No STX transfer - this uses ALEX tokens
    };
  };

  // HERMETICA - Deposit sBTC to hBTC vault
  // Reference: https://docs.hermetica.fi/hbtc/how-it-works/smart-contracts
  const buildHermeticaDeposit = (asset, amount, sender) => {
    const [contractAddress, contractName] = HERMETICA_CONTRACTS.vault_hbtc.split('.');
    
    // Post-condition: Allow sending sBTC (use sent_less_than_or_equal_to like successful txs)
    const postConditions = [
      Pc.principal(sender)
        .willSendLte(amount)
        .ft(SBTC_TOKEN, 'sbtc-token')
    ];
    
    // Hermetica deposit function takes:
    // 1. assets (uint) - amount of sBTC to deposit
    // 2. affiliate (optional buff 64) - optional affiliate code (we use none)
    return {
      contractAddress,
      contractName,
      functionName: 'deposit',
      functionArgs: [
        uintCV(amount),    // assets - amount of sBTC
        noneCV(),          // affiliate - none
      ],
      postConditions,
      // Use 'deny' mode like successful transactions
      postConditionMode: 'deny',
      stxAmount: 0 // sBTC deposit, no STX
    };
  };

  // BITFLOW - Add liquidity to STX-stSTX stableswap pool
  // Supports single-sided deposit (just STX or just stSTX)
  // This is a stableswap pool optimized for low slippage between STX and stSTX
  const buildBitflowDeposit = (asset, amount, sender) => {
    const [contractAddress, contractName] = BITFLOW_CONTRACTS.stableswap_stx_ststx.split('.');
    const [ststxAddr, ststxName] = STSTX_TOKEN.split('.');
    const [lpAddr, lpName] = BITFLOW_CONTRACTS.lp_token_stx_ststx.split('.');
    
    // For single-sided STX deposit:
    // x-amount-added = STX amount
    // y-amount-added = 0 (no stSTX)
    // min-lp-amount = 0 (accept any LP tokens, pool handles slippage)
    
    const stxAmount = asset === 'stx' ? amount : 0;
    const ststxAmount = asset === 'ststx' ? amount : 0;
    
    // Post-condition: user sends STX
    const postConditions = [];
    if (stxAmount > 0) {
      postConditions.push(
        Pc.principal(sender)
          .willSendLte(stxAmount)
          .ustx()
      );
    }
    if (ststxAmount > 0) {
      postConditions.push(
        Pc.principal(sender)
          .willSendLte(ststxAmount)
          .ft(STSTX_TOKEN, 'ststx')
      );
    }
    
    // add-liquidity(y-token, lp-token, x-amount-added, y-amount-added, min-lp-amount)
    return {
      contractAddress,
      contractName,
      functionName: 'add-liquidity',
      functionArgs: [
        contractPrincipalCV(ststxAddr, ststxName),    // y-token (stSTX trait)
        contractPrincipalCV(lpAddr, lpName),          // lp-token trait
        uintCV(stxAmount),                            // x-amount-added (STX)
        uintCV(ststxAmount),                          // y-amount-added (stSTX)
        uintCV(0),                                    // min-lp-amount (accept any)
      ],
      postConditions,
      postConditionMode: 'allow', // Allow mode for single-sided deposits
      stxAmount: stxAmount
    };
  };

  // VELAR DEX - Add liquidity to VELAR-STX pool
  // Velar uses UniV2-style AMM requiring paired liquidity
  // User must provide both VELAR tokens and STX in the pool's ratio
  const buildVelarDeposit = (asset, amount, sender) => {
    const [routerAddress, routerName] = VELAR_CONTRACTS.router.split('.');
    
    // For VELAR-STX pool:
    // - token0: VELAR token
    // - token1: wSTX (wrapped STX)
    // The ratio changes constantly as trades occur
    // We calculate based on a conservative ratio and let the contract adjust
    
    // Pool ratio calculation - use a conservative estimate
    // Current on-chain ratio is ~1822 VELAR per STX (but changes frequently)
    // We'll overestimate STX slightly and use high slippage tolerance
    const VELAR_PER_STX_RATIO = 1800; // Conservative ratio (slightly less than actual)
    const velarAmount = amount; // Amount is in VELAR (with 6 decimals)
    const stxRequired = Math.floor(velarAmount / VELAR_PER_STX_RATIO);
    
    // Use 5% slippage tolerance to account for ratio changes
    const amt0Min = Math.floor(velarAmount * 0.95);
    const amt1Min = Math.floor(stxRequired * 0.95);
    
    // Post-conditions for both tokens - use allow mode and set generous limits
    // The router will only take what's needed, not the full post-condition amount
    const postConditions = [
      // User sends VELAR tokens (allow up to requested amount)
      Pc.principal(sender)
        .willSendLte(velarAmount)
        .ft(VELAR_CONTRACTS.velar_token, 'velar'),
      // User sends STX - provide extra buffer for ratio fluctuations
      Pc.principal(sender)
        .willSendLte(Math.floor(stxRequired * 1.1)) // 10% buffer
        .ustx()
    ];
    
    // add-liquidity function signature:
    // (id uint) (token0 trait) (token1 trait) (lp-token trait)
    // (amt0-desired uint) (amt1-desired uint) (amt0-min uint) (amt1-min uint)
    return {
      contractAddress: routerAddress,
      contractName: routerName,
      functionName: 'add-liquidity',
      functionArgs: [
        uintCV(VELAR_STX_POOL_ID),                                    // pool id
        contractPrincipalCV(VELAR_DEPLOYER, 'velar-token'),           // token0 (VELAR)
        contractPrincipalCV(VELAR_DEPLOYER, 'wstx'),                  // token1 (wSTX)
        contractPrincipalCV(VELAR_DEPLOYER, 'velar-stx'),             // lp-token
        uintCV(velarAmount),                                          // amt0-desired (VELAR)
        uintCV(stxRequired),                                          // amt1-desired (STX)
        uintCV(amt0Min),                                              // amt0-min
        uintCV(amt1Min),                                              // amt1-min
      ],
      postConditions,
      postConditionMode: 'allow', // Allow mode since pool may adjust amounts
      stxAmount: stxRequired, // STX to wrap and add
      // Additional info for UI
      pairedDeposit: true,
      secondaryAmount: stxRequired,
      secondaryAsset: 'STX'
    };
  };

  // STXOSHI Vault - Treasury-based staking (25% APY)
  // This sends STXOSHI coins to the treasury wallet (custodial)
  // Yield is calculated and paid out manually by treasury owner
  // Manual retry to trigger wallet popup for deposits
  const handleRetryWalletPopup = async () => {
    setWalletPopupFailed(false);
    toast.info("Retrying wallet popup...");
    await handleAcceptRisk();
  };

  const buildSTXOSHIDeposit = (asset, amount, sender) => {
    // Post-condition: Allow sending STXOSHI to treasury
    const postConditions = [
      Pc.principal(sender)
        .willSendEq(amount)
        .ft(STXOSHI_TOKEN, STXOSHI_TOKEN_NAME)
    ];
    
    return {
      // This is a token transfer via the contract's transfer function
      type: 'treasury-deposit',
      contractAddress: STXOSHI_TOKEN.split('.')[0],
      contractName: STXOSHI_TOKEN.split('.')[1],
      treasuryAddress: STXOSHI_TREASURY,
      tokenContract: STXOSHI_TOKEN,
      tokenName: STXOSHI_TOKEN_NAME,
      amount,
      sender,
      postConditions,
      apy: STXOSHI_APY
    };
  };

  // Helper to extract error message from various API error formats
  const extractErrorMessage = (data, fallback = 'Unknown error') => {
    if (!data) return fallback;
    
    // If detail is a string, return it
    if (typeof data.detail === 'string') return data.detail;
    
    // If detail is an array (Pydantic validation errors)
    if (Array.isArray(data.detail)) {
      const firstError = data.detail[0];
      if (firstError && firstError.msg) {
        return firstError.msg;
      }
      return JSON.stringify(data.detail);
    }
    
    // If detail is an object with msg
    if (data.detail && typeof data.detail === 'object' && data.detail.msg) {
      return data.detail.msg;
    }
    
    // Try message field
    if (typeof data.message === 'string') return data.message;
    
    // Last resort - stringify
    if (data.detail) return JSON.stringify(data.detail);
    
    return fallback;
  };

  // Self-service deposit recovery
  const handleRecoverDeposit = async () => {
    if (!failedDeposit) return;
    
    setRecovering(true);
    
    // Use XMLHttpRequest for better Safari compatibility
    const xhr = new XMLHttpRequest();
    const url = `${API}/agents/${agentId}/defi/deposit/self-recover`;
    
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);
    
    xhr.onload = function() {
      setRecovering(false);
      try {
        const data = JSON.parse(xhr.responseText);
        
        if (xhr.status >= 200 && xhr.status < 300 && data.success) {
          toast.success(data.message || 'Deposit recovered successfully!', {
            description: data.position_id ? `Position ID: ${data.position_id.slice(0, 8)}...` : undefined,
            duration: 5000
          });
          
          // Clear the failed deposit
          setFailedDeposit(null);
          
          // Remove from localStorage
          const savedFailedDeposits = JSON.parse(localStorage.getItem('failedDeposits') || '[]');
          const updated = savedFailedDeposits.filter(d => d.txid !== failedDeposit.txid);
          localStorage.setItem('failedDeposits', JSON.stringify(updated));
          
        // Refresh positions
        onSuccess?.();
        } else {
          toast.error('Recovery failed', {
            description: extractErrorMessage(data, `HTTP ${xhr.status}`),
            duration: 8000
          });
        }
      } catch (parseErr) {
        console.error('Parse error:', parseErr, 'Response:', xhr.responseText);
        toast.error('Recovery failed', {
          description: xhr.responseText || 'Server error',
          duration: 8000
        });
      }
    };
    
    xhr.onerror = function() {
      setRecovering(false);
      console.error('XHR Error:', xhr.status, xhr.statusText);
      toast.error('Recovery failed', {
        description: 'Network error. Please check your connection and try again.',
        duration: 5000
      });
    };
    
    xhr.ontimeout = function() {
      setRecovering(false);
      toast.error('Recovery failed', {
        description: 'Request timed out. Please try again.',
        duration: 5000
      });
    };
    
    xhr.timeout = 30000; // 30 second timeout
    
    try {
      xhr.send(JSON.stringify({
        txid: failedDeposit.txid,
        protocol: failedDeposit.protocol,
        asset: failedDeposit.asset,
        amount: Number(failedDeposit.amount) || 10000000  // Ensure it's a number
      }));
    } catch (sendErr) {
      setRecovering(false);
      console.error('Send error:', sendErr);
      toast.error('Recovery failed', {
        description: sendErr.message || 'Failed to send request',
        duration: 5000
      });
    }
  };

  // Manual recovery with TX ID
  const handleManualRecovery = async () => {
    if (!manualTxId.trim()) {
      toast.error('Please enter a transaction ID');
      return;
    }
    
    setRecovering(true);
    try {
      const response = await fetch(`${API}/agents/${agentId}/defi/deposit/self-recover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          txid: manualTxId.trim(),
          protocol: protocol,
          asset: asset,
          amount: Math.round(parseFloat(amount || '0') * 1_000_000) // Convert to micro units
        })
      });
      
      // Read response text first to avoid body consumed issues
      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseErr) {
        console.error('Failed to parse response:', responseText);
        throw new Error(responseText || 'Server error');
      }
      
      if (!response.ok) {
        throw new Error(extractErrorMessage(data, `HTTP ${response.status}`));
      }
      
      if (data.success) {
        if (data.already_exists) {
          toast.success('Good news! This deposit was already recorded.', {
            description: `Status: ${data.status}`,
            duration: 5000
          });
        } else {
          toast.success(data.message || 'Deposit recovered successfully!', {
            description: `Position ID: ${data.position_id?.slice(0, 8)}...`,
            duration: 5000
          });
        }
        
        // Clear the form
        setManualTxId('');
        setShowManualRecovery(false);
        
        // Refresh positions
        onSuccess?.();
      } else {
        toast.error('Recovery failed', {
          description: extractErrorMessage(data, 'Please verify the TX ID and try again'),
          duration: 8000
        });
      }
    } catch (err) {
      console.error('Manual recovery error:', err);
      toast.error('Recovery failed', {
        description: typeof err.message === 'string' ? err.message : 'Network error. Please try again.',
        duration: 5000
      });
    } finally {
      setRecovering(false);
    }
  };

  return (
    <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <ArrowDownToLine className="w-5 h-5 text-green-400" />
        Deposit to DeFi
      </h3>
      
      <div className="space-y-4">
        {/* Protocol Selection */}
        <div>
          <label className="block text-sm text-zinc-400 mb-2">Protocol</label>
          
          {/* STXOSHI Vault - Featured at Top */}
          <button
            onClick={() => {
              setProtocol('stxoshi');
              setAsset('stxoshi');
            }}
            className={`relative w-full p-4 mb-3 rounded-lg border transition-all ${
              protocol === 'stxoshi' 
                ? 'bg-gradient-to-r from-amber-500 to-orange-600 border-transparent text-white' 
                : 'bg-zinc-900 border-amber-500/50 hover:border-amber-500 text-zinc-300'
            } ring-2 ring-amber-500/30`}
          >
            {/* High Yield Badge */}
            <div className="absolute -top-2 right-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-lg flex items-center gap-1">
              <span>🔥</span>
              <span>High Yield 25%</span>
            </div>
            <div className="flex items-center justify-center gap-3">
              <img 
                src="/stxoshi-logo.png" 
                alt="STXOSHI" 
                className="w-8 h-8 rounded-full"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <div className="text-center">
                <div className="font-bold text-base">STXOSHI Vault</div>
                <div className="text-xs opacity-75">Native STXOSHI coin staking</div>
              </div>
            </div>
          </button>
          
          {/* Other Protocols Grid */}
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(PROTOCOL_CONFIG)
              .filter(([key]) => key !== 'stxoshi') // Exclude STXOSHI from grid
              .map(([key, config]) => (
              <button
                key={key}
                onClick={() => {
                  setProtocol(key);
                  // Set default asset for each protocol
                  if (key === 'hermetica') {
                    setAsset('sbtc');
                  } else if (key === 'zest') {
                    setAsset('ststx');
                  } else if (key === 'alex') {
                    setAsset('alex');
                  } else {
                    setAsset('stx');
                  }
                }}
                className={`relative p-3 rounded-lg border transition-all ${
                  protocol === key 
                    ? `${config.color} border-transparent text-white` 
                    : 'bg-zinc-900 border-zinc-700 hover:border-zinc-500 text-zinc-300'
                }`}
              >
                <div className="font-medium text-sm">{config.name}</div>
                <div className="text-xs opacity-75 line-clamp-1">{config.description}</div>
              </button>
            ))}
          </div>
        </div>
        
        {/* Asset Selection */}
        <div>
          <label className="block text-sm text-zinc-400 mb-2">Asset</label>
          <select
            value={asset}
            onChange={(e) => setAsset(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white"
          >
            {getAssetOptions().map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          
          {/* Zest stSTX requirement note */}
          {protocol === 'zest' && (
            <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-xs text-blue-300">
              <p className="font-medium">💡 Zest V2 requires stSTX or sBTC</p>
              <p className="mt-1 text-blue-300/80">
                To deposit STX to Zest, first convert STX → stSTX via StackingDAO, then deposit stSTX here.
              </p>
            </div>
          )}
          
          {/* ALEX token requirement note */}
          {protocol === 'alex' && (
            <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-xs">
              <p className="font-medium text-amber-300">⚠️ ALEX Staking Unavailable</p>
              <p className="mt-1 text-amber-200/80">
                ALEX governance token staking via alex-reserve-pool appears to be inactive.
                Following the June 2025 security incident, this feature may have been deprecated.
              </p>
              <p className="mt-2 text-amber-200/60">
                Alternative: Use ALEX directly on app.alexlab.co for liquidity pools or trading.
              </p>
            </div>
          )}
          
          {/* STXOSHI High Yield note */}
          {protocol === 'stxoshi' && (
            <div className="mt-2 p-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-lg text-xs text-amber-200">
              <p className="font-medium flex items-center gap-2">
                <span>🔥</span>
                <span>STXOSHI Vault - 25% APY</span>
              </p>
              <p className="mt-1 text-amber-200/80">
                Stake your STXOSHI coins in the native vault for guaranteed 25% annual yield. 
                Yield is paid in STXOSHI coins when you close your position.
              </p>
            </div>
          )}
          
          {/* Velar Paired Liquidity note */}
          {protocol === 'velar' && (
            <div className="mt-2 p-3 bg-pink-500/10 border border-pink-500/30 rounded-lg text-xs text-pink-200">
              <p className="font-medium flex items-center gap-2">
                <span>🔗</span>
                <span>Paired Liquidity - VELAR + STX Required</span>
              </p>
              <p className="mt-1 text-pink-200/80">
                Velar uses AMM liquidity pools. You provide VELAR tokens and the equivalent STX will be added automatically.
                Ratio fluctuates with trades - we use a conservative estimate.
              </p>
              {amount && parseFloat(amount) > 0 && (
                <p className="mt-2 text-pink-100 font-medium">
                  📊 {parseFloat(amount).toLocaleString()} VELAR ≈ {(parseFloat(amount) / 1800).toFixed(2)} STX required (+ buffer)
                </p>
              )}
            </div>
          )}
          
          {/* Bitflow Single-Sided note */}
          {protocol === 'bitflow' && (
            <div className="mt-2 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-xs text-cyan-200">
              <p className="font-medium flex items-center gap-2">
                <span>🌊</span>
                <span>STX-stSTX Stableswap Pool</span>
              </p>
              <p className="mt-1 text-cyan-200/80">
                Bitflow's stableswap allows single-sided STX deposits. 
                Your STX provides liquidity alongside stSTX for low-slippage swaps.
                Earn trading fees from the pool.
              </p>
            </div>
          )}
        </div>
        
        {/* Amount Input */}
        <div>
          <label className="block text-sm text-zinc-400 mb-2">
            Amount ({asset.toUpperCase()})
          </label>
          <div className="relative">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={`0.00 ${asset.toUpperCase()}`}
              step="0.000001"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white"
            />
          </div>
        </div>
        
        {/* APY Display */}
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-zinc-400">Current APY</span>
            <span className="text-2xl font-bold text-green-400">
              {getCurrentApy().toFixed(2)}%
            </span>
          </div>
          <p className="text-xs text-zinc-500 mt-2">
            * APY is variable and not guaranteed
          </p>
        </div>
        
        {/* Deposit Button */}
        <button
          onClick={handleInitiateDeposit}
          disabled={loading || confirmingDeposit || !amount || !isConnected}
          className="w-full py-4 bg-green-600 hover:bg-green-500 disabled:bg-zinc-700 disabled:cursor-not-allowed rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-colors"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Sending Transaction...
            </>
          ) : confirmingDeposit ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Confirming Deposit...
            </>
          ) : !isConnected ? (
            <>
              <Wallet className="w-5 h-5" />
              Connect Wallet First
            </>
          ) : (
            <>
              <ArrowDownToLine className="w-5 h-5" />
              Deposit to {PROTOCOL_CONFIG[protocol].name}
            </>
          )}
        </button>
        
        {/* Blue pulsing retry button if wallet popup fails */}
        {walletPopupFailed && loading && (
          <button
            onClick={handleRetryWalletPopup}
            className="w-full mt-3 py-3 px-4 rounded-lg font-semibold text-white 
              bg-gradient-to-r from-cyan-500 to-blue-600 
              hover:from-cyan-400 hover:to-blue-500
              animate-pulse shadow-lg shadow-cyan-500/30
              flex flex-col items-center justify-center gap-1"
          >
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              <span>Stuck? Click to Manually Confirm</span>
            </div>
            <span className="text-xs font-normal opacity-90">
              Or try iPhone Xverse for smoother experience
            </span>
          </button>
        )}
        
        {/* Failed Deposit Recovery Panel */}
        {failedDeposit && (
          <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-yellow-400">Deposit Confirmation Failed</h4>
                <p className="text-xs text-zinc-400 mt-1">
                  Your transaction was sent on-chain but wasn't recorded in the app.
                  Your funds are safe! Click "Recover Deposit" to automatically verify and record it.
                </p>
                
                <div className="mt-3 space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500">Transaction ID:</span>
                    <a 
                      href={`https://explorer.hiro.so/txid/${failedDeposit.txid}?chain=mainnet`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                    >
                      {failedDeposit.txid.slice(0, 16)}...
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500">Amount:</span>
                    <span className="text-white">{failedDeposit.amountDisplay} {failedDeposit.asset.toUpperCase()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-500">Protocol:</span>
                    <span className="text-white">{PROTOCOL_CONFIG[failedDeposit.protocol]?.name || failedDeposit.protocol}</span>
                  </div>
                </div>
                
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={handleRecoverDeposit}
                    disabled={recovering}
                    className="flex-1 py-2 px-3 bg-green-600 hover:bg-green-500 disabled:bg-green-800 rounded-lg text-xs text-white font-semibold flex items-center justify-center gap-1"
                  >
                    {recovering ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Recovering...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-3 h-3" />
                        Recover Deposit
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(failedDeposit.txid);
                      toast.success('Transaction ID copied!');
                    }}
                    className="py-2 px-3 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-xs text-white flex items-center justify-center gap-1"
                  >
                    Copy TX ID
                  </button>
                  <button
                    onClick={() => setFailedDeposit(null)}
                    className="py-2 px-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-xs text-zinc-400"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Manual Recovery Link */}
        {!failedDeposit && (
          <div className="mt-3 text-center">
            <button
              onClick={() => setShowManualRecovery(!showManualRecovery)}
              className="text-xs text-zinc-500 hover:text-blue-400 underline"
            >
              {showManualRecovery ? 'Hide recovery form' : 'Have a failed deposit? Recover it here'}
            </button>
          </div>
        )}
        
        {/* Manual Recovery Form */}
        {showManualRecovery && !failedDeposit && (
          <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <h4 className="text-sm font-semibold text-blue-400 mb-2">Recover Failed Deposit</h4>
            <p className="text-xs text-zinc-400 mb-3">
              If your deposit succeeded on-chain but wasn't recorded in the app, 
              enter the transaction ID below. Make sure you've selected the correct protocol and asset above.
            </p>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Transaction ID</label>
                <input
                  type="text"
                  value={manualTxId}
                  onChange={(e) => setManualTxId(e.target.value)}
                  placeholder="0x19a87d4c62c42cfa667ceb974e806573..."
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
              
              <div className="text-xs text-zinc-500">
                <p>Selected: <span className="text-white">{PROTOCOL_CONFIG[protocol]?.name}</span> / <span className="text-white">{asset.toUpperCase()}</span></p>
                {amount && <p>Amount: <span className="text-white">{amount} {asset.toUpperCase()}</span></p>}
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={handleManualRecovery}
                  disabled={recovering || !manualTxId.trim()}
                  className="flex-1 py-2 px-3 bg-green-600 hover:bg-green-500 disabled:bg-zinc-700 rounded-lg text-sm text-white font-semibold flex items-center justify-center gap-2"
                >
                  {recovering ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Verify & Recover
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowManualRecovery(false);
                    setManualTxId('');
                  }}
                  className="py-2 px-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm text-zinc-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <RiskWarningModal
        isOpen={showRiskWarning}
        warning={riskWarning}
        onAccept={handleAcceptRisk}
        onDecline={() => setShowRiskWarning(false)}
      />
    </div>
  );
}

// Positions List
function PositionsList({ agentId, positions, onWithdraw, onRefresh, loading, showUsd, prices }) {
  const [refreshingPosition, setRefreshingPosition] = useState(null);
  const { getAuthHeaders } = useWallet();
  const API = process.env.REACT_APP_BACKEND_URL + '/api';
  const wholeUnits = (raw, asset) => {
    const div = (asset === 'stx' || asset === 'stxoshi' || asset === 'velar' || asset === 'ststx') ? 1_000_000 : 100_000_000;
    return (raw || 0) / div;
  };
  const assetUsd = (asset) => {
    const key = asset === 'ststx' ? 'stx' : asset;
    if (prices && prices[key]) return prices[key];
    if (asset === 'stxoshi') return STXOSHI_USD;
    return null;
  };
  const fmtAmt = (raw, asset) => {
    const whole = wholeUnits(raw, asset);
    if (showUsd) {
      const rate = assetUsd(asset);
      if (rate) {
        return `$${(whole * rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
      // No USD rate for this asset — fall back to native value.
      return `${whole.toLocaleString(undefined, { maximumFractionDigits: 4 })} ${asset.toUpperCase()}`;
    }
    return whole.toLocaleString(undefined, { maximumFractionDigits: 6 });
  };
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch {
      return "—";
    }
  };
  
  const handleRefreshStatus = async (position) => {
    if (!position.deposit_tx) return;
    
    setRefreshingPosition(position.id);
    try {
      const response = await fetch(
        `${API}/agents/${agentId}/defi/deposit/verify?txid=${position.deposit_tx}`,
        {
          method: 'GET',
          headers: getAuthHeaders()
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.position_status === 'active') {
          toast.success('Deposit confirmed!', {
            description: 'Your position is now active and earning yield.'
          });
          onRefresh?.();
        } else if (data.position_status === 'failed') {
          toast.error('Transaction failed', {
            description: data.message || 'The deposit transaction failed on-chain.'
          });
          onRefresh?.();
        } else {
          toast.info('Still pending', {
            description: 'The transaction is still being processed on the blockchain.'
          });
        }
      } else {
        toast.error('Check failed', {
          description: 'Could not verify transaction status.'
        });
      }
    } catch (err) {
      console.error('Error refreshing status:', err);
      toast.error('Error checking status');
    } finally {
      setRefreshingPosition(null);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }
  
  if (!positions || positions.length === 0) {
    return (
      <div className="text-center py-8 text-zinc-500">
        <Wallet className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No active DeFi positions</p>
        <p className="text-sm">Deposit funds to start earning yield</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {positions.map((pos) => {
        const config = PROTOCOL_CONFIG[pos.protocol] || {};
        const isActive = pos.status === 'active';
        const isPending = pos.status === 'pending';
        const isFailed = pos.status === 'failed';
        const isRefreshing = refreshingPosition === pos.id;
        
        return (
          <div 
            key={pos.id} 
            className={`bg-zinc-800/50 border rounded-xl p-4 ${
              isActive ? 'border-zinc-700' : 
              isPending ? 'border-yellow-500/50 bg-yellow-500/5' :
              isFailed ? 'border-red-500/50 bg-red-500/5' :
              'border-zinc-800 opacity-60'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${config.color} text-white`}>
                    {config.name}
                  </span>
                  <span className="text-zinc-400 text-sm">{pos.asset.toUpperCase()}</span>
                </div>
                <div className="mt-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 w-fit ${
                    isActive ? 'bg-green-500/20 text-green-400' : 
                    isPending ? 'bg-yellow-500/20 text-yellow-400' :
                    isFailed ? 'bg-red-500/20 text-red-400' :
                    'bg-zinc-700 text-zinc-400'
                  }`}>
                    {isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                    {isFailed && <XCircle className="w-3 h-3" />}
                    {isActive && <CheckCircle className="w-3 h-3" />}
                    {pos.status}
                  </span>
                </div>
              </div>
              
              <div className="text-right">
                <div className="text-lg font-semibold text-white">
                  {fmtAmt(pos.current_value, pos.asset)}
                </div>
                <div className="text-xs text-zinc-400">{showUsd ? 'USD' : pos.asset.toUpperCase()}</div>
              </div>
            </div>
            
            {/* Pending position message with refresh button */}
            {isPending && (
              <div className="mb-3 p-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-yellow-300 flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Awaiting blockchain confirmation...
                  </p>
                  <button
                    onClick={() => handleRefreshStatus(pos)}
                    disabled={isRefreshing}
                    className="px-2 py-1 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 text-xs rounded flex items-center gap-1 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                    {isRefreshing ? 'Checking...' : 'Check Status'}
                  </button>
                </div>
              </div>
            )}
            
            {/* Failed position message */}
            {isFailed && (
              <div className="mb-3 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-xs text-red-300 flex items-center gap-2">
                  <XCircle className="w-3 h-3" />
                  Transaction failed on-chain. {pos.failure_reason || 'Your funds were not transferred.'}
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-3 gap-2 text-xs mb-3">
              <div className="min-w-0">
                <div className="text-zinc-500 text-[10px]">Deposited</div>
                <div className="text-white truncate">
                  {fmtAmt(pos.amount_deposited, pos.asset)}
                </div>
              </div>
              <div className="min-w-0">
                <div className="text-zinc-500 text-[10px]">Yield</div>
                <div className={`truncate ${isActive ? 'text-green-400' : 'text-zinc-500'}`}>
                  {isActive ? '+' : ''}{fmtAmt(pos.yield_earned, pos.asset)}
                </div>
              </div>
              <div className="min-w-0 text-right">
                <div className="text-zinc-500 text-[10px]">APY</div>
                <div className="text-blue-400">
                  {pos.protocol === 'stxoshi' ? '25.0' : (pos.current_apy?.toFixed(1) || pos.apy?.toFixed(1))}%
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-3">
              <span data-testid={`position-opened-${pos.id}`}>Opened {fmtDate(pos.deposit_time || pos.deposited_at)}</span>
              {(pos.closed_at || pos.withdrawn_at) && (
                <span data-testid={`position-closed-${pos.id}`}>Closed {fmtDate(pos.closed_at || pos.withdrawn_at)}</span>
              )}
            </div>
            
            {isActive && (
              <button
                onClick={() => onWithdraw(pos)}
                className="w-full py-2 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-sm text-white flex items-center justify-center gap-2 transition-colors"
              >
                <ArrowUpFromLine className="w-4 h-4" />
                Withdraw
              </button>
            )}
            
            {pos.deposit_tx && (
              <a
                href={`https://explorer.hiro.so/txid/${pos.deposit_tx}?chain=mainnet`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300 mt-2"
              >
                <ExternalLink className="w-3 h-3" />
                View deposit TX
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Main DeFi Manager Component
export default function DeFiManager({ agentId, agentName }) {
  const { getAuthHeaders, signStxTransfer, signContractCall, addresses } = useWallet();
  const [positions, setPositions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [closeLoading, setCloseLoading] = useState(false);
  const { showUsd, toggleUsd, prices } = useCurrency();

  const fetchPositions = useCallback(async () => {
    try {
      const response = await fetch(`${API}/agents/${agentId}/defi/positions`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setPositions(data.positions || []);
        setSummary(data.summary || null);
      }
    } catch (error) {
      console.error('Error fetching positions:', error);
    } finally {
      setLoading(false);
    }
  }, [agentId, getAuthHeaders]);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  const handleWithdraw = async (position) => {
    setWithdrawing(true);
    
    try {
      // For STXOSHI vault, submit a withdrawal request to admin
      if (position.protocol === 'stxoshi') {
        const response = await fetch(
          `${API}/agents/${agentId}/defi/withdraw/request`,
          {
            method: 'POST',
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              position_ids: [position.id]
            })
          }
        );
        
        if (response.ok) {
          toast.success('Withdrawal Request Submitted!', {
            description: 'Your STXOSHI will be sent to your wallet by the treasury admin.',
            duration: 8000
          });
          fetchPositions();
        } else {
          const error = await response.json();
          toast.error('Withdrawal request failed', { description: error.detail || 'Please try again' });
        }
        return;
      }
      
      // Build withdrawal contract call based on protocol
      const withdrawCall = buildWithdrawContractCall(position);
      
      if (withdrawCall && signContractCall) {
        toast.info('Please approve the withdrawal in your wallet');
        
        const txResult = await signContractCall(withdrawCall);
        
        if (txResult && txResult.success && txResult.txid) {
          // Update position status in backend
          await fetch(
            `${API}/agents/${agentId}/defi/withdraw/confirm?position_ids=${position.id}&txid=${txResult.txid}`,
            {
              method: 'POST',
              headers: getAuthHeaders()
            }
          );
          
          toast.success('Withdrawal submitted!', {
            description: `TX: ${txResult.txid.slice(0, 16)}...`
          });
          
          fetchPositions();
          return;
        }
      }
      
      // Fallback: Direct to protocol UI for withdrawal
      const protocolUrls = {
        stackingdao: 'https://app.stackingdao.com',
        zest: 'https://app.zestprotocol.com',
        alex: 'https://app.alexlab.co',
        hermetica: 'https://app.hermetica.fi',
        bitflow: 'https://app.bitflow.finance',
        velar: 'https://app.velar.co',
      };
      
      const url = protocolUrls[position.protocol];
      
      toast.info('Withdrawal Instructions', {
        description: `To withdraw from ${PROTOCOL_CONFIG[position.protocol]?.name}, visit their app and connect your wallet`,
        duration: 10000
      });
      
      if (url) {
        window.open(url, '_blank');
      }
      
      fetchPositions();
    } catch (error) {
      console.error('Withdrawal error:', error);
      if (error.message.includes('rejected') || error.message.includes('denied')) {
        toast.error('Withdrawal cancelled', { description: 'You rejected the transaction' });
      } else {
        toast.error('Withdrawal failed', { description: error.message });
      }
    } finally {
      setWithdrawing(false);
    }
  };

  // Build withdrawal contract call based on protocol
  // Arguments should be Clarity values (uintCV, etc.) for openContractCall
  const buildWithdrawContractCall = (position) => {
    const { protocol, asset, amount_deposited } = position;
    
    switch (protocol) {
      case 'zest': {
        // Zest V2 uses market contract for withdrawals
        // Function: collateral-remove-redeem
        // Note: Only stSTX and sBTC are supported
        const tokenMap = {
          sbtc: TOKEN_CONTRACTS.sbtc,
          ststx: TOKEN_CONTRACTS.ststx,
        };
        const tokenContract = tokenMap[asset] || TOKEN_CONTRACTS.ststx;
        const [ftAddress, ftName] = tokenContract.split('.');
        const [contractAddress, contractName] = ZEST_CONTRACTS.market.split('.');
        
        return {
          contractAddress,
          contractName,
          functionName: 'collateral-remove-redeem',
          functionArgs: [
            contractPrincipalCV(ftAddress, ftName),  // ft - token contract
            uintCV(amount_deposited),                 // shares to redeem
            uintCV(1),                                // min-assets (slippage protection)
            noneCV(),                                 // price-feeds (optional)
          ]
        };
      }
      case 'stackingdao': {
        // StackingDAO uses init-withdraw to request withdrawal
        // Arguments: reserve, direct-helpers, ststx-amount
        const STACKINGDAO_BASE = "SP4SZE494VC2YC5JYG7AYFQ44F5Q4PYV7DVMDPBG";
        const [contractAddress, contractName] = STACKINGDAO_CONTRACTS.core_v6.split('.');
        return {
          contractAddress,
          contractName,
          functionName: 'init-withdraw',
          functionArgs: [
            contractPrincipalCV(STACKINGDAO_BASE, "reserve-v1"),        // reserve
            contractPrincipalCV(STACKINGDAO_BASE, "direct-helpers-v4"), // direct-helpers  
            uintCV(amount_deposited),                                    // ststx-amount
          ]
        };
      }
      case 'alex': {
        const [contractAddress, contractName] = ALEX_CONTRACTS.alex_reserve_pool.split('.');
        return {
          contractAddress,
          contractName,
          functionName: 'remove-from-position',
          functionArgs: [
            uintCV(amount_deposited),
          ]
        };
      }
      case 'hermetica': {
        // Hermetica uses request-redeem for withdrawals (2-step process)
        // 1. request-redeem: Request redemption, get claim-id
        // 2. redeem: After cooldown, claim the sBTC
        const [contractAddress, contractName] = HERMETICA_CONTRACTS.vault_hbtc.split('.');
        return {
          contractAddress,
          contractName,
          functionName: 'request-redeem',
          functionArgs: [
            uintCV(amount_deposited),  // shares to redeem
            // is-express: false = standard withdrawal (lower fee, longer cooldown)
            boolCV(false),
          ]
        };
      }
      case 'bitflow': {
        const [contractAddress, contractName] = BITFLOW_CONTRACTS.stx_pool.split('.');
        return {
          contractAddress,
          contractName,
          functionName: 'remove-liquidity',
          functionArgs: [
            uintCV(amount_deposited),
          ]
        };
      }
      case 'velar': {
        const [contractAddress, contractName] = VELAR_CONTRACTS.router.split('.');
        return {
          contractAddress,
          contractName,
          functionName: 'remove-liquidity',
          functionArgs: [
            uintCV(VELAR_STX_POOL_ID),
            contractPrincipalCV(VELAR_DEPLOYER, 'velar-token'),
            contractPrincipalCV(VELAR_DEPLOYER, 'wstx'),
            contractPrincipalCV(VELAR_DEPLOYER, 'velar-stx'),
            uintCV(amount_deposited),  // lp-token-amount
            uintCV(0),                  // amt0-min
            uintCV(0),                  // amt1-min
          ]
        };
      }
      case 'stxoshi': {
        // STXOSHI is a custodial vault - withdrawals are processed by admin
        // Return null to trigger the admin withdrawal request flow
        return null;
      }
      default:
        return null;
    }
  };

  const handleCloseAgent = async () => {
    setCloseLoading(true);
    
    try {
      // Confirm close - this creates a withdrawal request
      const confirmResponse = await fetch(
        `${API}/agents/${agentId}/close/confirm`,
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );
      
      const result = await confirmResponse.json();
      
      if (confirmResponse.ok) {
        toast.success('Withdrawal request submitted!', {
          description: `${(result.amount_to_receive / 1_000_000).toFixed(6)} STX will be sent to your wallet shortly.`,
          duration: 10000
        });
        setShowCloseConfirm(false);
        fetchPositions();
      } else {
        throw new Error(result.detail || 'Failed to close agent');
      }
    } catch (error) {
      console.error('Close error:', error);
      toast.error('Failed to close agent', { description: error.message });
    } finally {
      setCloseLoading(false);
    }
  };

  // Determine asset label based on positions
  const getAssetLabel = () => {
    const activePositions = positions.filter(p => p.status === 'active');
    if (activePositions.length === 0) return '';
    const allStxoshi = activePositions.every(p => p.protocol === 'stxoshi');
    if (allStxoshi) return 'STXOSHI';
    if (activePositions.length === 1) return activePositions[0].asset.toUpperCase();
    return ''; // Mixed positions - no specific label
  };

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      {summary && summary.active_positions > 0 && (
        <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 border border-green-500/30 rounded-xl p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-6 h-6 text-green-400" />
            <h3 className="text-lg font-semibold text-white">Portfolio Summary</h3>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-zinc-400 text-sm">Active Positions</div>
              <div className="text-2xl font-bold text-white">{summary.active_positions}</div>
            </div>
            <div>
              <div className="text-zinc-400 text-sm">Total Value</div>
              <div className="text-2xl font-bold text-white">
                {(summary.total_value / 1_000_000).toFixed(4)} {getAssetLabel()}
              </div>
            </div>
            <div>
              <div className="text-zinc-400 text-sm">Total Yield</div>
              <div className="text-2xl font-bold text-green-400">
                +{(summary.total_yield_earned / 1_000_000).toFixed(6)} {positions.filter(p => p.status === 'active').every(p => p.protocol === 'stxoshi') && 'STXOSHI'}
              </div>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setShowCloseConfirm(true)}
                className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/50 rounded-lg text-red-400 text-sm flex items-center gap-2"
              >
                <XCircle className="w-4 h-4" />
                Close Agent
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid md:grid-cols-2 gap-6">
        {/* Deposit Form */}
        <DepositForm 
          agentId={agentId} 
          onSuccess={fetchPositions} 
          getAuthHeaders={getAuthHeaders}
        />
        
        {/* Positions */}
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-400" />
            Active Positions
            <button
              onClick={toggleUsd}
              className="ml-auto text-xs px-2.5 py-1 rounded-full border border-zinc-600 text-zinc-300 hover:bg-zinc-700/60 transition-colors"
              data-testid="positions-usd-toggle"
              title="Toggle between STXOSHI and USD"
            >
              {showUsd ? "USD" : "STXOSHI"}
            </button>
          </h3>
          
          <PositionsList
            agentId={agentId}
            positions={positions.filter(p => p.status === 'active' || p.status === 'pending')}
            onWithdraw={handleWithdraw}
            onRefresh={fetchPositions}
            loading={loading || withdrawing}
            showUsd={showUsd}
            prices={prices}
          />
        </div>
      </div>
      
      {/* Close Agent Confirmation Modal */}
      {showCloseConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-zinc-900 border border-red-500/50 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <XCircle className="w-8 h-8 text-red-500" />
              <h2 className="text-xl font-bold text-white">Close Agent?</h2>
            </div>
            
            <p className="text-zinc-400 mb-4">
              This will withdraw all funds from DeFi protocols and close the agent "{agentName}".
            </p>
            
            {summary && (
              <div className="bg-zinc-800 rounded-lg p-4 mb-4">
                <div className="flex justify-between mb-2">
                  <span className="text-zinc-400">Total to receive:</span>
                  <span className="text-white font-semibold">
                    {(summary.total_value / 1_000_000).toFixed(4)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Including yield:</span>
                  <span className="text-green-400">
                    +{(summary.total_yield_earned / 1_000_000).toFixed(6)}
                  </span>
                </div>
              </div>
            )}
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowCloseConfirm(false)}
                disabled={closeLoading}
                className="flex-1 py-3 px-4 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCloseAgent}
                disabled={closeLoading}
                className="flex-1 py-3 px-4 bg-red-600 hover:bg-red-500 rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-colors"
              >
                {closeLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Closing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Confirm Close
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
